import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Table,
  Button,
  Dropdown,
  Menu,
  Input,
  InputNumber,
  Row,
  Col,
  Tag,
  Typography,
  Card,
  List,
  Grid,
  Avatar,
  Tooltip,
  Divider,
  Select,
  Checkbox,
  DatePicker,
  Drawer,
  Space,
  Space as AntSpace,
  MenuProps,
  Popconfirm,
  Modal,
  Form,
  Switch,
} from 'antd';
import { DownOutlined, SearchOutlined, InfoCircleOutlined, FileExcelOutlined } from '@ant-design/icons';
import ProposalService from '../../services/ProposalService';
import AuthorizedPersonService from '../../services/AuthorizedPersonService';
import {
  capitalizeWords,
  formatDate,
  formatToTL,
  formatToTRY,
  hasRole,
  normalizeTurkish,
  turkishLowerCase,
  openNotification,
  extractApiErrorMessage,
  COMPANY_INFO_WARNING_MESSAGE,
  isEmailSendDateDisabled,
  getEmailSendDisabledTime,
  isAccountActive,
} from '../../utils';
import CompanyService from '../../services/CompanyService';
import StatusService from '../../modules/Status/Service/StatusService';
import tinycolor from 'tinycolor2';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import debounce from 'lodash/debounce';
import PlanningModal from '../Companies/PlanningModal';
import AuthorizedPersonTable from '../Companies/AuthorizedPersonTable';
import DocumentUploadModal from '../Companies/UploadModal';
import './ProposalCompanySelect.scss';
import ProposalView from './ProposalView';
import ReportInvoiceModal from '../ProjectFollowUp/ReportInvoiceModal';
import AttributeService from '../../services/AttributeService';
import SettingService, { SettingItem } from '../../services/SettingService';
import DiscountModal from './DiscountModal';
import AssigneeSummary from './AssigneeSummary';
import NegativeProposalReasonModal from './NegativeProposalReasonModal';
import NegativeProposalReasonService, {
  INegativeProposalReason,
} from '../../services/NegativeProposalReasonService';
import { getProposalFollowUpLink } from '../../utils/proposalLinks';
import FieldEffortDisplay from './FieldEffortDisplay';
import { hasMatchingAuthorizedRecipients } from '../../utils/proposalAuthorizedRecipients';
import RenewalSettingsDrawer, { RenewalSettingsFormValues } from './RenewalSettingsDrawer';
import ProposalProcessHistoryDrawer from './ProposalProcessHistoryDrawer';

// Türkçe dil/format ayarları
import dayjs, { Dayjs } from 'dayjs';
import locale from 'antd/es/date-picker/locale/tr_TR';
import 'dayjs/locale/tr';
import UserService from '../../services/UserService';
import ApproveDocument from '../Companies/ApproveDocument';
import { UserListIcon } from 'hugeicons-react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { UserIcon } from 'hugeicons-react';
import { sendProposalWithExistingPdf } from './sendProposalWithExistingPdf';
import PdfUpload from './PdfUpload';
import {
  buildOpportunityProposalCreateLink,
  getProposalStatusId,
} from '../Opportunity/opportunityLinks';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { TextArea } = Input;
const PDF_ARCHIVE_REFRESH_WARNING_MESSAGE =
  'Teklif tutarı güncellendi ancak teklif PDF’i yeniden oluşturulamadı. Lütfen tekrar deneyin.';
const DISCOUNT_PDF_REFRESH_DELAY_MS = 3000;

type NegativeReasonFormValues = {
  negativeReasonId?: number;
  negativeReasonText?: string;
  sendNegativeSurvey?: boolean;
};

type CancelProposalFormValues = {
  cancellationNote: string;
};

type NegativeReasonActionType = 'reject' | 'updateReason';

const ASSIGNEE_SETTING_KEYS = [
  'PROPOSAL_DEFAULT_OFFER_ASSIGNE_USER_ID',
  'PROPOSAL_DEFAULT_PLAN_ASSIGNE_USER_ID',
  'PROPOSAL_DEFAULT_LEGAL_ASSIGNE_USER_ID',
  'PROPOSAL_DEFAULT_REPORT_ASSIGNE_USER_ID',
  'PROPOSAL_DEFAULT_INVOICE_ASSIGNE_USER_ID',
  'PROPOSAL_DEFAULT_FINANCE_ASSIGNE_USER_ID',
] as const;

type AssigneeFormValues = {
  offerAssigneeUserId?: string;
  planAssigneeUserId?: string;
  legalAssigneeUserId?: string;
  reportAssigneeUserId?: string;
  invoiceAssigneeUserId?: string;
  financeAssigneeUserId?: string;
};

const ASSIGNEE_SETTING_FIELD_MAP: Record<typeof ASSIGNEE_SETTING_KEYS[number], keyof AssigneeFormValues> = {
  PROPOSAL_DEFAULT_OFFER_ASSIGNE_USER_ID: 'offerAssigneeUserId',
  PROPOSAL_DEFAULT_PLAN_ASSIGNE_USER_ID: 'planAssigneeUserId',
  PROPOSAL_DEFAULT_LEGAL_ASSIGNE_USER_ID: 'legalAssigneeUserId',
  PROPOSAL_DEFAULT_REPORT_ASSIGNE_USER_ID: 'reportAssigneeUserId',
  PROPOSAL_DEFAULT_INVOICE_ASSIGNE_USER_ID: 'invoiceAssigneeUserId',
  PROPOSAL_DEFAULT_FINANCE_ASSIGNE_USER_ID: 'financeAssigneeUserId',
};

const ASSIGNEE_SETTING_TITLE_MAP: Record<string, string> = {
  PROPOSAL_DEFAULT_OFFER_ASSIGNE_USER_ID: 'Teklif Sorumlusu',
  PROPOSAL_DEFAULT_PLAN_ASSIGNE_USER_ID: 'Plan Sorumlusu',
  PROPOSAL_DEFAULT_LEGAL_ASSIGNE_USER_ID: 'Yasal Bildirim Sorumlusu',
  PROPOSAL_DEFAULT_REPORT_ASSIGNE_USER_ID: 'Rapor Sorumlusu',
  PROPOSAL_DEFAULT_INVOICE_ASSIGNE_USER_ID: 'Fatura Sorumlusu',
  PROPOSAL_DEFAULT_FINANCE_ASSIGNE_USER_ID: 'Finans Sorumlusu',
};

const STATUS_FOLLOWUP_ROUTES: Record<string, string> = {
  'Kontrol Tamamlandı': '/project-followup?tab=5',
  'Kontrol ve Raporlama': '/project-followup?tab=4',
  'Planlama': '/project-followup?tab=2',
  'Sözleşme Yüklendi': '/project-followup?tab=1',
  'Yasal Bildirim (İSG-Katip)': '/project-followup?tab=3',
};
const FOLLOW_UP_SUMMARY_STATUS_IDS = [3, 12];
const EMPTY_FOLLOW_UP_SUMMARY = [
  {
    statusId: 'sent-and-manual-follow',
    statusName: '',
    totalPrice: 0,
    machineMinutes: 0,
    electricMinutes: 0,
    hygieneMinutes: 0,
  },
];

const buildFollowUpSummary = (summaryData: any[]) => {
  const relevantSummaries = summaryData.filter((item: any) =>
    FOLLOW_UP_SUMMARY_STATUS_IDS.includes(Number(item?.statusId))
  );

  return [
    {
      ...EMPTY_FOLLOW_UP_SUMMARY[0],
      totalPrice: relevantSummaries.reduce((total, item) => total + (Number(item?.totalPrice) || 0), 0),
      machineMinutes: relevantSummaries.reduce((total, item) => total + (Number(item?.machineMinutes) || 0), 0),
      electricMinutes: relevantSummaries.reduce((total, item) => total + (Number(item?.electricMinutes) || 0), 0),
      hygieneMinutes: relevantSummaries.reduce((total, item) => total + (Number(item?.hygieneMinutes) || 0), 0),
    },
  ];
};

const isCustomerCompanyProposal = (proposal: any): boolean =>
  proposal?.company?.account?.roles?.some((role: any) => role?.name === 'CUSTOMER') ?? false;

const MIN_PROPOSAL_SEARCH_LENGTH = 2;

const getEffectiveProposalSearchText = (value?: string | null) => {
  const normalizedValue = value ? turkishLowerCase(value).trim() : '';
  return normalizedValue.length >= MIN_PROPOSAL_SEARCH_LENGTH ? normalizedValue : '';
};

const ProposalList: React.FC<any> = ({ defaultStatusIds = [], type = 1 }) => {
  const [searchParams] = useSearchParams();
  const initialSearchFromUrl = searchParams.get('search');
  const [filteredProposals, setFilteredProposals] = useState<any[]>([]);
  const [searchText, setSearchText] = useState(getEffectiveProposalSearchText(initialSearchFromUrl));
  const proposalListAbortControllerRef = useRef<AbortController | null>(null);
  const [isDiscountModalVisible, setIsDiscountModalVisible] = useState(false);
  const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState<any | null>(null);
  const [statuses, setStatuses] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1); // Başlangıç sayfasını 1 olarak ayarladık
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const navigate = useNavigate();
  const [isSendProposalModalVisible, setSendProposalModalVisible] = React.useState(false);
  const [sendProposalScheduleAt, setSendProposalScheduleAt] = React.useState<Dayjs | null>(null);
  const [sendProposalLoading, setSendProposalLoading] = React.useState(false);
  const [attributes, setAttributes] = useState<any>();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState(0);
  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [followUpSummary, setFollowUpSummary] = useState<any[]>(EMPTY_FOLLOW_UP_SUMMARY);
  const summaryRequestId = useRef(0);
  const [exporting, setExporting] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [documentModalType, setDocumentModalType] = useState(1);
  const [isCentralUser, setIsCentralUser] = useState(false);
  const cityIdsToShow = [2170, 2195, 2150, 2167, 2151, 2176, 2163, 2218, 2175];
  const [isDocumentModalVisible, setIsDocumentModalVisible] = useState(false);
  const [isAuthorizedDrawerVisible, setIsAuthorizedDrawerVisible] = useState(false);
  const [authorizedCompanyId, setAuthorizedCompanyId] = useState<number | null>(null);
  const [authorizedProposalId, setAuthorizedProposalId] = useState<number | null>(null);
  const [selectedAuthorizedIds, setSelectedAuthorizedIds] = useState<number[]>([]);
  const [assigneeModalVisible, setAssigneeModalVisible] = useState(false);
  const [assigneeModalLoading, setAssigneeModalLoading] = useState(false);
  const [assigneeUpdateLoading, setAssigneeUpdateLoading] = useState(false);
  const [assigneeSettings, setAssigneeSettings] = useState<SettingItem[]>([]);
  const [assigneeTargetProposal, setAssigneeTargetProposal] = useState<any | null>(null);
  const [assignableUsers, setAssignableUsers] = useState<any[]>([]);
  const [assignableUsersAll, setAssignableUsersAll] = useState<any[]>([]);
  const [assigneeForm] = Form.useForm<AssigneeFormValues>();
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [rejectingProposal, setRejectingProposal] = useState<any | null>(null);
  const [rejectActionType, setRejectActionType] = useState<NegativeReasonActionType>('reject');
  const [rejectInitialValues, setRejectInitialValues] = useState<NegativeReasonFormValues | undefined>(undefined);
  const [cancelProposalModalVisible, setCancelProposalModalVisible] = useState(false);
  const [cancelProposalLoading, setCancelProposalLoading] = useState(false);
  const [cancelProposalTarget, setCancelProposalTarget] = useState<any | null>(null);
  const [cancelProposalForm] = Form.useForm<CancelProposalFormValues>();
  const canSeeProposalNotes = hasRole('ADMIN') || hasRole('CENTRAL_USER') || hasRole('FINANCE');
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  const [districts, setDistricts] = useState<{ id: number; name: string }[]>([]);
  const [cities, setCities] = useState<{ id: number, name: string }[]>([]);

  const [selectedCityId, setSelectedCityId] = useState<number | null>(null);
  const [selectedDistrictId, setSelectedDistrictId] = useState<number | null>(null);


  const [customerList, setCustomerList] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
  const [currentUserMaxDiscountRate, setCurrentUserMaxDiscountRate] = useState<number | null>(null);
  const [isDiscountSubmitLoading, setIsDiscountSubmitLoading] = useState(false);
  const [discountPdfArchiveProposalId, setDiscountPdfArchiveProposalId] = useState<number | null>(null);
  const [isNotesDrawerOpen, setIsNotesDrawerOpen] = useState(false);
  const [noteTargetProposal, setNoteTargetProposal] = useState<any | null>(null);
  const [isDueDayModalVisible, setIsDueDayModalVisible] = useState(false);
  const [dueDayTargetProposal, setDueDayTargetProposal] = useState<any | null>(null);
  const [dueDayUpdateLoading, setDueDayUpdateLoading] = useState(false);
  const [dueDayForm] = Form.useForm<{ dueDay?: number }>();
  const [renewalTrackingUpdateId, setRenewalTrackingUpdateId] = useState<number | null>(null);
  const [renewalSettingsOpen, setRenewalSettingsOpen] = useState(false);
  const [renewalSettingsTargetProposal, setRenewalSettingsTargetProposal] = useState<any | null>(null);
  const [renewalSettingsLoading, setRenewalSettingsLoading] = useState(false);

  const handleCustomerChange = (value: number) => {
    setSelectedCustomerId(value);
  };

  const handleCityChange = (value: number) => {
    const city = cities.find((c) => c.id === value);
    setSelectedCityId(value);
    setSelectedDistrictId(null);
    setDistricts([]);
    if (value) {
      getDistricts(value);
    }
  };

  const openUploadModal = (proposalId: number) => {
    setSelectedProposalId(proposalId);
    setIsUploadModalVisible(true);
    setDocumentModalType(2);
  };

  const handleDistrictChange = (value: number) => {
    setSelectedDistrictId(value);
  };

  const [companyList, setCompanyList] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);

  const handleCompanyChange = (value: number) => {
    setSelectedCompanyId(value);
  };

  const handleOpenNotesDrawer = (proposal: any) => {
    setNoteTargetProposal(proposal);
    setIsNotesDrawerOpen(true);
  };

  const handleCloseNotesDrawer = () => {
    setIsNotesDrawerOpen(false);
    setNoteTargetProposal(null);
  };

  const showDrawer = () => {
    setOpen(true);
  };

  const onClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (!isDocumentModalVisible) {
      setTimeout(() => {
        fetchProposals();
      }, 1000);
    }
  }, [isDocumentModalVisible])

  useEffect(() => {
    AttributeService.getAll()
      .then((response) => {
        setAttributes(response.data);
      })
      .catch((error) => {
        console.error('Error fetching attributes:', error);
      });
    getCustomerAll();
  }, [])

  // Logo için cache/pending
  const logoCache = new Map();
  const pendingRequests = new Map();

  // Planlama modal
  const [selectedProposalId, setSelectedProposalId] = useState<number | null>(null);
  const [isPlanningModalVisible, setIsPlanningModalVisible] = useState(false);

  // Durumlar
  const [selectedStatusIds, setSelectedStatusIds] = useState<number[]>([]);
  const [selectedNegativeReasonIds, setSelectedNegativeReasonIds] = useState<number[]>([]);
  const [selectedRenewal, setSelectedRenewal] = useState<boolean | null>(null);
  const [negativeReasons, setNegativeReasons] = useState<INegativeProposalReason[]>([]);

  // "proposal.show" izni kontrolü için:
  const [permissions, setPermissions] = useState<string[]>([]);

  // Sıralama
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('DESC');

  // Sadece kendi tekliflerimi listele
  const [onlyMyCompanies, setOnlyMyCompanies] = useState<boolean>(false);
  const userId = localStorage.getItem('id');
  const isFinance = hasRole('FINANCE');
  const isCustomer = hasRole('CUSTOMER');

  // Rapor & Fatura Modal
  const [invoiceModalData, setInvoiceModalData] = useState<any>();
  const [invoiceModalType, setInvoiceModalType] = useState<any>();
  const [isInvoiceModalVisible, setIsInvoiceModalVisible] = useState(false);

  // Tarih aralığı
  const [dateRange, setDateRange] = useState<[string | null, string | null]>([null, null]);

  // Kullanıcı "İş Ortağı" mı?
  const isPartner = localStorage.getItem('isPartner');

  // 2) Statüleri çek
  useEffect(() => {
    StatusService.getAll()
      .then((response: any) => {
        setStatuses(response.data);
      })
      .catch((error) => console.error('Statüler yüklenirken hata oluştu:', error));
  }, [defaultStatusIds]);

  useEffect(() => {
    NegativeProposalReasonService.listAll({ 'isActive.equals': true })
      .then((response) => {
        setNegativeReasons(response.data);
      })
      .catch((error) => {
        console.error('Olumsuzluk nedenleri yüklenirken hata oluştu:', error);
      });
  }, []);

  const getCustomerAll = () => {
    UserService.getAllCustomer()
      .then((response: any) => {
        setCustomerList(response.data);
      })
      .catch((error) => console.error('Statüler yüklenirken hata oluştu:', error));
  }
  const openDocumentModal = (proposalId: number) => {
    setSelectedProposalId(proposalId);
    setIsDocumentModalVisible(true);
  };

  const getCompanyAll = () => {
    CompanyService.getCompanyAll()
      .then((response: any) => {
        setCompanyList(response.data);
      })
      .catch((error) => console.error('Statüler yüklenirken hata oluştu:', error));
  }

  const getCities = () => {
    CompanyService.getStates(225)
      .then((response: any) => {
        setCities(response.data);
      })
  }

  const getDistricts = async (stateId: number) => {
    try {
      const response = await CompanyService.getDistricts(stateId);
      setDistricts(response.data);
    } catch (error) {
      console.error("İlçeler alınırken hata oluştu:", error);
    }
  };

  const reorderAssigneeSettings = (items: SettingItem[]): SettingItem[] => {
    const orderMap = new Map(ASSIGNEE_SETTING_KEYS.map((key, index) => [key, index]));
    return items
      .filter((item) => item?.key && orderMap.has(item.key as typeof ASSIGNEE_SETTING_KEYS[number]))
      .sort(
        (a, b) =>
          (orderMap.get(a.key as typeof ASSIGNEE_SETTING_KEYS[number]) ?? 99) -
          (orderMap.get(b.key as typeof ASSIGNEE_SETTING_KEYS[number]) ?? 99),
      );
  };

  const buildAssigneeInitialValues = (proposal: any, settings: SettingItem[]): AssigneeFormValues => {
    const values: AssigneeFormValues = {};
    const settingMap = new Map(settings.map((item) => [item.key, item.value]));
    ASSIGNEE_SETTING_KEYS.forEach((key) => {
      const fieldName = ASSIGNEE_SETTING_FIELD_MAP[key];
      const proposalValue = proposal ? proposal[fieldName] : null;
      const settingValue = settingMap.get(key);
      const resolved = proposalValue != null ? String(proposalValue) : settingValue || undefined;
      (values as any)[fieldName] = resolved || undefined;
    });
    return values;
  };

  const loadAssignableUsers = useCallback(async () => {
    try {
      const resp: any = await UserService.getUsersWithoutPagination({});
      const list: any[] = resp?.data?.content ?? resp?.data ?? [];
      const roleFiltered = list.filter((u) => {
        const roles: any[] = u?.roles ?? [];
        const roleNames = roles.map((r: any) => r?.name).filter(Boolean);
        return !roleNames.includes('INSPECTOR') && !roleNames.includes('CUSTOMER');
      });
      const activeOnly = roleFiltered.filter((u) => isAccountActive(u));
      setAssignableUsersAll(roleFiltered);
      setAssignableUsers(activeOnly);
      return activeOnly;
    } catch (error) {
      console.error('Assignable users could not be loaded', error);
      return [];
    }
  }, []);

  useEffect(() => {

    const isUserAdmin = hasRole('ADMIN');
    setIsAdmin(isUserAdmin);

    const isUserCentralUser = hasRole('CENTRAL_USER');
    setIsCentralUser(isUserCentralUser);

    if (isUserAdmin || isUserCentralUser) {
      getCustomerAll();
      getCompanyAll();
    }

    getCities();

  }, []);

  useEffect(() => {
    loadAssignableUsers();
  }, [loadAssignableUsers]);

  useEffect(() => {
    let isMounted = true;

    const fetchMaxDiscountRate = async () => {
      if (hasRole('ADMIN')) {
        if (isMounted) {
          setCurrentUserMaxDiscountRate(null);
        }
        return;
      }

      try {
        const response = await UserService.getMe();
        if (!isMounted) return;
        const rate = response?.data?.maxDiscountRate;
        setCurrentUserMaxDiscountRate(
          typeof rate === 'number' && !Number.isNaN(rate) ? rate : null
        );
      } catch (error) {
        if (isMounted) {
          console.error('Kullanıcı indirim oranı alınırken hata oluştu:', error);
          setCurrentUserMaxDiscountRate(null);
        }
      }
    };

    fetchMaxDiscountRate();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    fetchProposals();
  }, [
    currentPage,
    pageSize,
    sortField,
    sortDirection,
    isPlanningModalVisible,
    isUploadModalVisible,
    searchText,
    defaultStatusIds,
    filter,
    // selectedStatusIds,
    // onlyMyCompanies,
    userId,
    // dateRange,
    permissions
  ]);

  // 4) Upload modal kapanınca tekrar veriyi çek
  useEffect(() => {
    if (!isUploadModalVisible) {
      fetchProposals();
      fetchFollowUpSummary();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUploadModalVisible]);

  useEffect(() => {
    setCurrentPage(1);
  }, [onlyMyCompanies]);

  const buildRequestParams = useCallback(
    (withPagination: boolean = true) => {
      let selectedCity: any = "";
      if (selectedCityId) {
        selectedCity = cities.find(city => city.id === selectedCityId);
      }

      let selectedDistrict: any = "";
      if (selectedDistrictId) {
        selectedDistrict = districts.find(district => district.id === selectedDistrictId);
      }

      let selectedCityName = selectedDistrictId === null ? (selectedCity ? normalizeTurkish(selectedCity?.name) : "") : "";
      const [start, end] = dateRange;

      const params: any = {
        company: normalizeTurkish(searchText),
        statuIds: selectedStatusIds.length > 0
          ? selectedStatusIds.join(',')
          : (defaultStatusIds?.length > 0 ? defaultStatusIds.join(',') : ""),
        sort: sortField,
        sortField: sortField,
        sortDirection: sortDirection,
        accountId: selectedCustomerId ? selectedCustomerId : "",
        companyId: selectedCompanyId ? selectedCompanyId : "",
        city: selectedCityName,
        district: selectedDistrict ? normalizeTurkish(selectedDistrict?.name) : "",
        direction: sortDirection,
        startDate: start,
        endDate: end,
        ...(selectedNegativeReasonIds.length > 0 && {
          negativeReasonIds: selectedNegativeReasonIds.join(','),
        }),
        ...(selectedRenewal !== null && { renewal: selectedRenewal }),
      };

      if (withPagination) {
        params.page = currentPage - 1; // 0-based
        params.size = pageSize;
      }

      if (onlyMyCompanies && userId) {
        params.accountId = userId;
      }

      return params;
    },
    [
      cities,
      currentPage,
      dateRange,
      defaultStatusIds,
      districts,
      onlyMyCompanies,
      pageSize,
      searchText,
      selectedCityId,
      selectedCompanyId,
      selectedCustomerId,
      selectedDistrictId,
      selectedNegativeReasonIds,
      selectedRenewal,
      selectedStatusIds,
      sortDirection,
      sortField,
      userId,
    ]
  );

  const canViewFollowUpSummary = type === 1 && (isAdmin || isCentralUser);
  const canViewFollowUpTotalPrice = type === 1 && isAdmin;
  const canViewFollowUpEffort = type === 1 && (isAdmin || isCentralUser);

  const fetchFollowUpSummary = useCallback(async () => {
    if (!canViewFollowUpSummary) {
      setFollowUpSummary(EMPTY_FOLLOW_UP_SUMMARY);
      setSummaryLoading(false);
      return;
    }

    const requestId = summaryRequestId.current + 1;
    summaryRequestId.current = requestId;
    setSummaryLoading(true);

    try {
      const response = await ProposalService.getFollowUpSummary(buildRequestParams(false));
      if (summaryRequestId.current !== requestId) {
        return;
      }

      const summaryData = Array.isArray(response?.data) ? response.data : [];
      setFollowUpSummary(buildFollowUpSummary(summaryData));
    } catch (error) {
      if (summaryRequestId.current === requestId) {
        setFollowUpSummary(EMPTY_FOLLOW_UP_SUMMARY);
      }
      console.error('Teklif takip özet toplamları yüklenirken hata oluştu:', error);
    } finally {
      if (summaryRequestId.current === requestId) {
        setSummaryLoading(false);
      }
    }
  }, [buildRequestParams, canViewFollowUpSummary]);

  useEffect(() => {
    fetchFollowUpSummary();
  }, [
    fetchFollowUpSummary,
  ]);

  const fetchProposals = async () => {

    const isCustomer = hasRole('CUSTOMER');

    if (!isCustomer) {
      if (permissions?.length === 0) {
        return false;
      }
    }

    setLoading(true);
    pendingRequests.clear();
    proposalListAbortControllerRef.current?.abort();
    const abortController = new AbortController();
    proposalListAbortControllerRef.current = abortController;

    const params = buildRequestParams(true);

    // "proposal.show" iznine göre getAll veya getMe
    let fetchFn;
    if (permissions.includes('proposal.show')) {
      fetchFn = ProposalService.getAll;
    } else {
      fetchFn = ProposalService.getMe;
    }

    try {
      const response: any = await fetchFn(params, { signal: abortController.signal });
      if (proposalListAbortControllerRef.current !== abortController || abortController.signal.aborted) {
        return;
      }
      // Gelen verileri enrichProposalsWithLogos ile zenginleştir
      const enrichedProposals = await enrichProposalsWithLogos(response.data.content);
      if (proposalListAbortControllerRef.current !== abortController || abortController.signal.aborted) {
        return;
      }

      // type === 2 => sadece statusId != null ise (örnek senaryo)
      const filteredByStatus =
        type === 2
          ? enrichedProposals.filter((pr: any) => pr.statusId != null)
          : enrichedProposals;

      setFilteredProposals(filteredByStatus);
      setTotalItems(response.data.totalElements);
    } catch (error: any) {
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
        return;
      }
      console.error('Teklif verileri alınırken hata oluştu:', error);
    } finally {
      if (proposalListAbortControllerRef.current === abortController) {
        proposalListAbortControllerRef.current = null;
        setLoading(false);
      }
    }
  };

  const handleCloseAssigneeModal = () => {
    setAssigneeModalVisible(false);
    setAssigneeTargetProposal(null);
    assigneeForm.resetFields();
  };

  const openAssigneeModal = async (proposal: any) => {
    setAssigneeTargetProposal(proposal);
    setAssigneeModalVisible(true);
    setAssigneeModalLoading(true);
    try {
      const [settings, users] = await Promise.all([
        SettingService.listSettings(),
        assignableUsers.length === 0 ? loadAssignableUsers() : Promise.resolve(assignableUsers),
      ]);
      const orderedSettings = reorderAssigneeSettings(settings || []);
      setAssigneeSettings(orderedSettings);
      const initialValues = buildAssigneeInitialValues(proposal, orderedSettings);
      assigneeForm.setFieldsValue(initialValues);
      if (!assignableUsers.length && Array.isArray(users)) {
        setAssignableUsers(users);
      }
    } catch (error) {
      console.error('Sorumlu listesi yüklenirken hata oluştu', error);
      openNotification('error', 'Hata', 'Sorumlu listesi yüklenemedi.');
      handleCloseAssigneeModal();
    } finally {
      setAssigneeModalLoading(false);
    }
  };

  const handleAssigneeUpdate = async () => {
    if (!assigneeTargetProposal) {
      return;
    }
    try {
      const values = await assigneeForm.validateFields();
      const payload = {
        offerAssigneeUserId: values.offerAssigneeUserId ? Number(values.offerAssigneeUserId) : null,
        planAssigneeUserId: values.planAssigneeUserId ? Number(values.planAssigneeUserId) : null,
        legalAssigneeUserId: values.legalAssigneeUserId ? Number(values.legalAssigneeUserId) : null,
        reportAssigneeUserId: values.reportAssigneeUserId ? Number(values.reportAssigneeUserId) : null,
        invoiceAssigneeUserId: values.invoiceAssigneeUserId ? Number(values.invoiceAssigneeUserId) : null,
        financeAssigneeUserId: values.financeAssigneeUserId ? Number(values.financeAssigneeUserId) : null,
      };
      setAssigneeUpdateLoading(true);
      await ProposalService.updateAssignees(assigneeTargetProposal.id, payload);
      openNotification('success', 'Başarılı', 'Sorumlular güncellendi.');
      handleCloseAssigneeModal();
      fetchProposals();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      openNotification('error', 'Hata', extractApiErrorMessage(error, 'Sorumlular güncellenemedi.'));
    } finally {
      setAssigneeUpdateLoading(false);
    }
  };

  const openDueDayModal = (proposal: any) => {
    setDueDayTargetProposal(proposal);
    setIsDueDayModalVisible(true);
    dueDayForm.setFieldsValue({ dueDay: proposal?.dueDay ?? 8 });
  };

  const handleCloseDueDayModal = () => {
    setIsDueDayModalVisible(false);
    setDueDayTargetProposal(null);
    dueDayForm.resetFields();
  };

  const handleDueDayUpdate = async () => {
    if (!dueDayTargetProposal) {
      return;
    }
    try {
      const values = await dueDayForm.validateFields();
      setDueDayUpdateLoading(true);
      await ProposalService.update(dueDayTargetProposal.id, { dueDay: values.dueDay });
      openNotification('success', 'Başarılı', 'Vade günü güncellendi.');
      handleCloseDueDayModal();
      fetchProposals();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      openNotification('error', 'Hata', extractApiErrorMessage(error, 'Vade günü güncellenemedi.'));
    } finally {
      setDueDayUpdateLoading(false);
    }
  };

  const updateProposalRenewalTracking = async (proposalId: number, checked: boolean) => {
    try {
      setRenewalTrackingUpdateId(proposalId);
      await ProposalService.update(proposalId, { renewal: checked });
      setFilteredProposals((prev) =>
        prev.map((proposal) =>
          Number(proposal?.id) === proposalId
            ? { ...proposal, renewal: checked }
            : proposal
        )
      );
      openNotification('success', 'Başarılı', 'Yenileme takibi güncellendi.');
    } catch (error: any) {
      openNotification(
        'error',
        'Hata',
        extractApiErrorMessage(error, 'Yenileme takibi güncellenemedi.')
      );
    } finally {
      setRenewalTrackingUpdateId(null);
    }
  };

  const openRenewalSettingsDrawer = (proposal: any) => {
    setRenewalSettingsTargetProposal(proposal);
    setRenewalSettingsOpen(true);
  };

  const closeRenewalSettingsDrawer = () => {
    setRenewalSettingsOpen(false);
    setRenewalSettingsTargetProposal(null);
  };

  const handleRenewalSettingsSubmit = async (values: RenewalSettingsFormValues) => {
    if (!renewalSettingsTargetProposal?.id) {
      openNotification('error', 'Hata', "Teklif ID'si bulunamadı.");
      return;
    }

    try {
      setRenewalSettingsLoading(true);
      await ProposalService.updateRenewalSettings(renewalSettingsTargetProposal.id, {
        renewal: values.renewal ?? false,
        renewalDate: values.renewalDate?.format('YYYY-MM-DD'),
        note: values.note?.trim() || undefined,
      });
      openNotification('success', 'Başarılı', 'Yenileme ayarları güncellendi.');
      closeRenewalSettingsDrawer();
      fetchProposals();
    } catch (error: any) {
      openNotification('error', 'Hata', extractApiErrorMessage(error, 'Yenileme ayarları güncellenemedi.'));
    } finally {
      setRenewalSettingsLoading(false);
    }
  };

  const handleRenewalTrackingToggle = (record: any, checked: boolean) => {
    const proposalId = Number(record?.id);
    if (!Number.isFinite(proposalId) || proposalId <= 0) {
      return;
    }

    Modal.confirm({
      title: checked
        ? 'Teklifi yenileme takibine dahil etmek istediğinize emin misiniz?'
        : 'Teklifi yenileme takibinden çıkarmak istediğinize emin misiniz?',
      okText: 'Evet',
      cancelText: 'Hayır',
      onOk: async () => updateProposalRenewalTracking(proposalId, checked),
    });
  };

  const handleTableChange = (pagination: any, _filters: any, sorter: any) => {
    setSortField(sorter.field || 'createdAt');
    setSortDirection(sorter.order === 'ascend' ? 'ASC' : 'DESC');
  };

  const getStatusNameById = (statusId: number) => {
    const status = statuses.find((s: any) => s.id === statusId);
    return status ? status.name : 'Bilinmiyor';
  };

  const getStatusColorById = (statusId: number) => {
    const status = statuses.find((s: any) => s.id === statusId);
    return status ? `${status.color}` : '#fdfdfd';
  };

  const minutesToDaysCeil = (minutes: number) => {
    if (minutes <= 0) return '-';
    return `${Math.ceil(minutes / 480)}`;
  };
  const computeOfferingsForExport = (record: any) => {
    const proposalAttributes = record?.attributes || [];
    let totalMakineMin = 0;
    let totalIsHijyeniMin = 0;
    let totalElektrikMin = 0;

    proposalAttributes.forEach((pa: any) => {
      const matched = attributes?.find((a: any) => a?.id === pa?.attributeId);
      if (!matched) return;
      const fieldTime = matched.fieldControlTime || 0;
      const qty = pa.quantity || 0;
      const period = pa.period || 0;
      const partial = fieldTime * (period * qty);
      if (matched.offering?.name === 'Makine') {
        totalMakineMin += partial;
      } else if (matched.offering?.name === 'İş Hijyeni') {
        totalIsHijyeniMin += partial;
      } else if (matched.offering?.name === 'Elektrik') {
        totalElektrikMin += partial;
      }
    });

    let totalMakinePrice = 0;
    let totalIsHijyeniPrice = 0;
    let totalElektrikPrice = 0;
    const logs = record?.priceCalculationResponse?.offeringLogs || [];
    logs.forEach((log: any) => {
      if (log.offeringName === 'Makine') {
        totalMakinePrice += log.finalPrice || 0;
      } else if (log.offeringName === 'İş Hijyeni') {
        totalIsHijyeniPrice += log.finalPrice || 0;
      } else if (log.offeringName === 'Elektrik') {
        totalElektrikPrice += log.finalPrice || 0;
      }
    });

    const totalPriceSum = totalMakinePrice + totalElektrikPrice + totalIsHijyeniPrice;
    if (totalPriceSum > 0) {
      const discountedPrice = record?.discountedPrice || 0;
      const oranMakine = totalMakinePrice / totalPriceSum;
      const oranElektrik = totalElektrikPrice / totalPriceSum;
      const oranHijyen = totalIsHijyeniPrice / totalPriceSum;
      const indirimMakine = Math.round(discountedPrice * oranMakine);
      const indirimElektrik = Math.round(discountedPrice * oranElektrik);
      const indirimHijyen = discountedPrice - indirimMakine - indirimElektrik;
      totalMakinePrice = totalMakinePrice - indirimMakine;
      totalElektrikPrice = totalElektrikPrice - indirimElektrik;
      totalIsHijyeniPrice = totalIsHijyeniPrice - indirimHijyen;
    }

    return {
      machinePrice: totalMakinePrice,
      hygienePrice: totalIsHijyeniPrice,
      electricPrice: totalElektrikPrice,
      machineDays: minutesToDaysCeil(totalMakineMin),
      hygieneDays: minutesToDaysCeil(totalIsHijyeniMin),
      electricDays: minutesToDaysCeil(totalElektrikMin),
    };
  };

  const handleExportExcel = useCallback(async () => {
    const isCustomer = hasRole('CUSTOMER');
    if (!isCustomer && permissions?.length === 0) {
      return;
    }
    try {
      setExporting(true);
      const params = buildRequestParams(false);
      const fetchFn = permissions.includes('proposal.show')
        ? ProposalService.getAllWithoutPagination
        : ProposalService.getMeWithoutPagination;
      const response: any = await fetchFn(params);
      const data: any[] = Array.isArray(response?.data) ? response.data : [];
      const exportData = data.map((proposal: any) => {
        const offering = computeOfferingsForExport(proposal);
        const addressTitle = proposal?.address?.title ?? '';
        const stateName = proposal?.address?.state || '';
        const cityName = proposal?.address?.city || '';
        const location = [stateName, cityName].filter((s: string) => !!s).join(' / ');
        const accountName = proposal?.account
          ? `${proposal.account.firstName || ''} ${proposal.account.lastName || ''}`.trim()
          : '';
        return {
          'Teklif Numarası': proposal?.proposalNumber ?? '',
          'Teklif Tarihi': proposal?.createdAt ? formatDate(proposal.createdAt) : '',
          'Firma Adı': proposal?.company?.name ?? '',
          'Adres Başlığı': addressTitle,
          'İl / İlçe': location,
          'Teklif Tutarı': typeof proposal?.totalPrice === 'number' ? formatToTL(proposal.totalPrice) : '-',
          'Makine Tutarı': offering.machinePrice ? formatToTL(offering.machinePrice) : '-',
          'Makine Uzman / Gün': offering.machineDays,
          'Elektrik Tutarı': offering.electricPrice ? formatToTL(offering.electricPrice) : '-',
          'Elektrik Uzman / Gün': offering.electricDays,
          'İş Hijyeni Tutarı': offering.hygienePrice ? formatToTL(offering.hygienePrice) : '-',
          'İş Hijyeni Uzman / Gün': offering.hygieneDays,
          'Teklifi Hazırlayan Kullanıcı Adı Soyadı': accountName,
          'Statü': getStatusNameById(proposal?.statusId),
        };
      });
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Teklifler');
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
      saveAs(blob, 'teklifler.xlsx');
    } catch (error) {
      console.error('Teklifler dışa aktarılırken hata oluştu:', error);
      openNotification('error', 'Hata', 'Teklifler dışa aktarılırken bir hata oluştu');
    } finally {
      setExporting(false);
    }
  }, [buildRequestParams, permissions, getStatusNameById, attributes]);

  // Logo zenginleştirme
  const enrichProposalsWithLogos = async (proposals: any[]) => {
    return Promise.all(
      proposals.map(async (proposal) => {
        const { company } = proposal;
        if (company.imageId) {
          if (logoCache.has(company.imageId)) {
            return {
              ...proposal,
              company: { ...company, logoUrl: logoCache.get(company.imageId) },
            };
          } else if (pendingRequests.has(company.imageId)) {
            await pendingRequests.get(company.imageId);
            return {
              ...proposal,
              company: { ...company, logoUrl: logoCache.get(company.imageId) },
            };
          } else {
            const logoPromise = CompanyService.getImage(company.imageId)
              .then((logoResponse) => {
                const logoUrl = logoResponse.data.preSignUrl;
                logoCache.set(company.imageId, logoUrl);
                pendingRequests.delete(company.imageId);
                return logoUrl;
              })
              .catch((error) => {
                console.error('Logo yüklenirken hata oluştu:', error);
                pendingRequests.delete(company.imageId);
                return null;
              });

            pendingRequests.set(company.imageId, logoPromise);
            const logoUrl = await logoPromise;
            return { ...proposal, company: { ...company, logoUrl } };
          }
        }
        return proposal;
      })
    );
  };

  const canSendProposal = async (record: any) => {
    try {
      if (!record) return true;
      const hasOffer = hasMatchingAuthorizedRecipients(record, 'OFFER');
      if (!hasOffer) {
        openNotification('warning', 'Uyarı', 'Teklif adresi ile eşleşen ve TEKLİF iletişim konusu seçili yetkili kişi bulunmamaktadır.');
      }
      return hasOffer;
    } catch (e) {
      // Servis hatasında gönderimi engelleme
      return true;
    }
  };

  const handleMenuClick = async (key: string, record: any) => {
    switch (key) {
      case 'download':
        break;
      case 'revise':
        handleOfferEditNavigation(record.id);
        break;
      case 'applyDiscount':
        setSelectedProposal(record);
        setIsDiscountModalVisible(true);
        break;
      case 'rejectProposal':
        openRejectModal(record);
        break;
      case 'updateNegativeReason':
        openRejectModal(record, 'updateReason');
        break;
      case 'cancelProposal':
        openCancelProposalModal(record);
        break;
      case 'rejectContract':
        handleUpdateStatus(record.id, 10);
        break;
      case 'cancelContract':
        try {
          await ProposalService.cancelContract(record.id);
          openNotification('success', 'Başarılı', 'Sözleşme iptal edildi.');
          fetchProposals();
        } catch (error) {
          const message = extractApiErrorMessage(error, 'Sözleşme iptal edilirken hata oluştu.');
          openNotification('error', 'Hata', message);
        }
        break;
      case 'manualTracing':
        handleUpdateStatus(record.id, 12);
        break;
      case 'uploadDocument':
        setSelectedProposalId(record.id);
        setSelectedProposal(record);
        setIsUploadModalVisible(true);
        setDocumentModalType(1);
        break;
      case 'requestPlanningDate':
        setSelectedProposalId(record.id);
        setIsPlanningModalVisible(true);
        break;
      case 'sendProposal': {
        const ok = await canSendProposal(record);
        if (!ok) return;
        setSelectedProposal(record);
        setSendProposalScheduleAt(null);
        setSendProposalModalVisible(true);
        break;
      }
      default:
        break;
    }
  };

  const openRejectModal = (proposal: any, actionType: NegativeReasonActionType = 'reject') => {
    setRejectingProposal(proposal);
    setRejectActionType(actionType);
    if (actionType === 'updateReason') {
      setRejectInitialValues({
        negativeReasonId: proposal?.negativeReason?.id ?? proposal?.negativeReasonId,
        negativeReasonText: proposal?.negativeReasonText ?? undefined,
      });
    } else {
      setRejectInitialValues(undefined);
    }
    setRejectModalVisible(true);
    setRejectLoading(false);
  };

  const handleRejectModalCancel = () => {
    setRejectModalVisible(false);
    setRejectingProposal(null);
    setRejectActionType('reject');
    setRejectInitialValues(undefined);
    setRejectLoading(false);
  };

  const handleRejectSubmit = async (values: NegativeReasonFormValues) => {
    if (!rejectingProposal) {
      return;
    }
    setRejectLoading(true);
    try {
      const payload = {
        negativeReasonId: values.negativeReasonId,
        negativeReasonText: values.negativeReasonText?.trim(),
        sendNegativeSurvey: values.sendNegativeSurvey ?? false,
      };
      await ProposalService.updateStatus(rejectingProposal.id, 2, {
        ...payload,
      });
      const isSurveyOnlyReject =
        rejectActionType !== 'updateReason'
        && Boolean(values.sendNegativeSurvey)
        && !values.negativeReasonId;
      openNotification(
        'success',
        'Başarılı',
        rejectActionType === 'updateReason'
          ? 'Olumsuz teklif nedeni güncellendi.'
          : isSurveyOnlyReject
            ? 'Teklif olumsuz sonlandırıldı ve anket e-postası gönderildi.'
            : 'Teklif olumsuz sonlandırıldı.',
      );
      handleRejectModalCancel();
      fetchProposals();
    } catch (error) {
      const message = extractApiErrorMessage(
        error,
        rejectActionType === 'updateReason'
          ? 'Olumsuz teklif nedeni güncellenirken hata oluştu.'
          : 'Olumsuz sonlandırma sırasında hata oluştu.',
      );
      openNotification('error', 'Hata', message);
    } finally {
      setRejectLoading(false);
    }
  };

  const openCancelProposalModal = (proposal: any) => {
    setCancelProposalTarget(proposal);
    cancelProposalForm.setFieldsValue({
      cancellationNote: proposal?.cancellationNote ?? '',
    });
    setCancelProposalModalVisible(true);
    setCancelProposalLoading(false);
  };

  const handleCancelProposalModalClose = () => {
    setCancelProposalModalVisible(false);
    setCancelProposalTarget(null);
    setCancelProposalLoading(false);
    cancelProposalForm.resetFields();
  };

  const handleCancelProposalSubmit = async () => {
    if (!cancelProposalTarget) {
      return;
    }
    try {
      const values = await cancelProposalForm.validateFields();
      setCancelProposalLoading(true);
      await ProposalService.updateStatus(cancelProposalTarget.id, 13, {
        cancellationNote: values.cancellationNote.trim(),
      });
      openNotification('success', 'Başarılı', 'Teklif iptal edildi.');
      handleCancelProposalModalClose();
      fetchProposals();
    } catch (error) {
      const isFormValidationError =
        typeof error === 'object' && error !== null && 'errorFields' in error;
      if (isFormValidationError) {
        return;
      }
      const message = extractApiErrorMessage(error, 'Teklif iptal edilirken bir hata oluştu.');
      openNotification('error', 'Hata', message);
    } finally {
      setCancelProposalLoading(false);
    }
  };

  const handleSendProposalConfirm = async () => {
    if (!selectedProposal) {
      return;
    }
    setSendProposalLoading(true);
    try {
      const invoiceEmailSendAt = sendProposalScheduleAt
        ? dayjs(sendProposalScheduleAt).add(3, 'hour').toISOString()
        : null;
      const sent = await sendProposalWithExistingPdf(selectedProposal.id, invoiceEmailSendAt);

      if (sent) {
        setSendProposalModalVisible(false);
        setSendProposalScheduleAt(null);
        fetchProposals();
      }
    } finally {
      setSendProposalLoading(false);
    }
  };

  const handleUpdateStatus = async (id: any, statusId: any, _type: any = 0) => {
    try {

      console.log("statusId2", statusId);
      await ProposalService.updateStatus(id, statusId);

      if (statusId !== 3) {
        if (_type === 1) {
          openNotification('success', 'Başarılı', 'Teklif Gönderildi.');
        } else {
          openNotification('success', 'Başarılı', 'Teklif statüsü güncellendi.');
        }
      }


      if (statusId === 14) {
        openNotification('success', 'Başarılı', 'Sözleşme Onayı İptal Edildi');
      }

      fetchProposals();
    } catch (error) {
      const message = extractApiErrorMessage(error, 'Statü değiştirilirken bir hata oluştu.');
      const isCompanyWarning = message === COMPANY_INFO_WARNING_MESSAGE;
      openNotification(isCompanyWarning ? 'warning' : 'error', isCompanyWarning ? 'Uyarı' : 'Hata', message);
      console.error(error);
    }
  };

  // Modal: İndirim Uygula
  const handleDiscountSubmit = async ({
    newTotalPrice,
  }: {
    discountAmount: number;
    newTotalPrice: number;
  }) => {
    if (!selectedProposal || isDiscountSubmitLoading) return;
    setIsDiscountSubmitLoading(true);
    try {
      await ProposalService.updatePrice(selectedProposal.id, {
        newTotalPrice,
      });

      await new Promise((resolve) => setTimeout(resolve, DISCOUNT_PDF_REFRESH_DELAY_MS));
      setDiscountPdfArchiveProposalId(selectedProposal.id);
    } catch (error) {
      setIsDiscountSubmitLoading(false);
      console.error('Fiyat güncellenirken hata oluştu:', error);
      const message = extractApiErrorMessage(error, 'Fiyat güncellenirken hata oluştu.');
      openNotification('error', 'Hata', message);
    }
  };

  const handleDiscountPdfArchiveSuccess = () => {
    setDiscountPdfArchiveProposalId(null);
    setIsDiscountSubmitLoading(false);
    setIsDiscountModalVisible(false);
    fetchProposals();
    openNotification('success', 'Başarılı', 'İndirim uygulandı.');
  };

  const handleDiscountPdfArchiveError = (error: unknown) => {
    console.error('Teklif PDF’i yeniden oluşturulamadı:', error);
    setDiscountPdfArchiveProposalId(null);
    setIsDiscountSubmitLoading(false);
    setIsDiscountModalVisible(false);
    fetchProposals();
    openNotification('warning', 'Uyarı', PDF_ARCHIVE_REFRESH_WARNING_MESSAGE);
  };

  const handleModalCancel = () => {
    if (isDiscountSubmitLoading) return;
    setIsDiscountModalVisible(false);
    setIsUploadModalVisible(false);
    setIsPlanningModalVisible(false);
  };

  // Rapor & Fatura Modal'a açılış
  const handleOpenInvoiceModal = (proposal: any, mode: 'edit' | 'view') => {
    if (!proposal.id) {
      console.error("Proposal ID is missing");
      openNotification("error", "Hata", "Teklif ID'si bulunamadı.");
      return;
    }

    setSelectedProposalId(proposal.id);
    setInvoiceModalData({
      invoiceAmount: proposal.invoiceAmount || '',
      reportUrl: proposal.reportUrl || '',
      financialInfoUrl: proposal.financialInfoUrl || '',
      invoiceDate: proposal.invoiceDate || '',
    });

    setIsInvoiceModalVisible(true);
    setInvoiceModalType(mode);
  };

  const openAuthorizedDrawer = async (record: any) => {
    try {
      const companyId = record?.company?.id;
      const proposalId = record?.id;
      if (!companyId || !proposalId) return;
      setAuthorizedCompanyId(companyId);
      setAuthorizedProposalId(proposalId);
      // Fetch proposal to preload selected authorized persons
      const resp: any = await ProposalService.get(proposalId);
      const ids: number[] = resp?.data?.authorizedPersonIds || [];
      setSelectedAuthorizedIds(ids);
      setIsAuthorizedDrawerVisible(true);
    } catch (e) {
      console.error('Yetkili kişi verileri alınamadı', e);
      openNotification('error', 'Hata', 'Yetkili kişi bilgileri alınamadı.');
    }
  };

  const handleSaveAuthorizedPersons = async () => {
    if (!authorizedProposalId) return;
    try {
      await ProposalService.updateAuthorizedPersons(authorizedProposalId, { authorizedPersonIds: selectedAuthorizedIds });
      openNotification('success', 'Başarılı', 'Yetkili kişiler kaydedildi.');
      setIsAuthorizedDrawerVisible(false);
      fetchProposals();
    } catch (e) {
      console.error('Yetkili kişiler kaydedilemedi', e);
      openNotification('error', 'Hata', 'Yetkili kişiler kaydedilemedi.');
    }
  };

  const renderPriceTooltipContent = (priceCalculationResponse: any) => {
    if (!priceCalculationResponse || !priceCalculationResponse.offeringLogs) {
      return <Text>Veri mevcut değil</Text>;
    }

    return (
      <div style={{ maxHeight: '300px', overflowY: 'auto', padding: '10px' }}>
        {priceCalculationResponse.offeringLogs.map((log: any, index: number) => (
          <div key={index} style={{ marginBottom: '15px' }}>
            <strong>{log.offeringName}</strong>
            <ul style={{ paddingLeft: '20px', marginTop: '10px' }}>
              {log.attributes.map((attribute: any, idx: number) => (
                <li key={idx}>
                  <span>
                    {attribute.attributeName} - {attribute.quantity} adet -{' '}
                    {formatToTL(attribute.price)}
                  </span>
                  <p style={{ color: '#999', fontSize: '12px' }}>
                    {attribute.description}
                  </p>
                </li>
              ))}
            </ul>
            {log.serviceFeeDifference > 0 && (
              <p style={{ color: '#f0ad4e' }}>
                Servis Fiyatı Farkı: {formatToTL(log.serviceFeeDifference)}
              </p>
            )}
            <p>Toplam: {formatToTL(log.finalPrice)}</p>
            {index !== priceCalculationResponse.offeringLogs.length - 1 && (
              <Divider style={{ background: 'gray', height: '2px' }} />
            )}
          </div>
        ))}
      </div>
    );
  };

  // Tablo kolonları
  const columns = [
    {
      title: 'Firma Adı',
      dataIndex: ['company', 'name'],
      key: 'companyName',
      render: (text: any, record: any) => {
        const combinedText = `${record?.address?.state || ''}${record?.address?.state && record?.address?.city ? '/' : ''}${record?.address?.city || ''}`;
        const controlTitle = record?.address?.title
        return (
          <div className="d-flex" style={{ alignItems: 'center' }}>
            <div className="avatar-image-logo" style={{ marginRight: 10 }}>
              <Avatar
                className="avatar-image-logo"
                style={{ backgroundColor: '#f0f0f0', color: '#000', cursor: 'pointer' }}
                onClick={() => record?.company?.id && navigate(`/companies/${record.company.id}`)}
              >
                {record?.company?.name.charAt(0).toUpperCase()}
              </Avatar>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
              <Tooltip
                placement="topLeft"
                title={
                  <>
                    {text.toUpperCase()} <br />
                    {controlTitle && <>{controlTitle}<br /></>}
                    {combinedText}
                  </>
                }
              >
                <Text
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: 'inline-block',
                    width: '220px',
                    cursor: 'pointer',
                    fontWeight: 600,
                  }}
                  onClick={() => record?.company?.id && navigate(`/companies/${record.company.id}`)}
                >
                  {text.toUpperCase()}
                </Text>
              </Tooltip>
              {controlTitle && (
                <div style={{ color: '#666', fontSize: 12 }}>
                  {controlTitle.length > 60 ? (
                    <Tooltip title={controlTitle}>
                      <span>{controlTitle.slice(0, 60)}...</span>
                    </Tooltip>
                  ) : (
                    <span>{controlTitle}</span>
                  )}
                </div>
              )}

            </div>
          </div>
        );
      },
    },
    {
      title: 'Teklif Tutarı',
      dataIndex: 'totalPrice',
      key: 'totalPrice',
      width: 120,
      sorter: true,
      render: (price: number, record: any) =>
        price ? (
          <>
            <Tooltip
              placement="topLeft"
              color="white"
              overlayStyle={{ maxWidth: 400 }}
              overlayInnerStyle={{ width: 400 }}
              title={<FieldEffortDisplay record={record} attributes={attributes} />}
            >
              <div className="price-tooltip width-120px">
                <span><b>{formatToTL(price)}</b></span>
              </div>
            </Tooltip>
          </>

        ) : (
          <span>-</span>
        ),
    },
    {
      title: 'Teklif Tarihi',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: true,
      render: (date: string) =>
        date ? <div className="width-120px"><Text>{formatDate(date)}</Text></div> : <span>-</span>,
    },
    {
      title: 'Teklif',
      dataIndex: 'proposalNumber',
      key: 'proposalNumber',
      sorter: true,
      render: (proposalNumber: string, record: any) =>
        proposalNumber ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
            {permissions.includes('proposal.show') && (
              <Tooltip placement="topLeft" title="Teklifi Hazırlayan">
                <div style={{ color: '#666', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <UserIcon size={14} />
                  <span>
                    {record.account
                      ? `${record.account.firstName} ${record.account.lastName}`
                      : 'Bilgi Yok'}
                  </span>
                </div>
              </Tooltip>
            )}
            {(() => {
              const targetUrl = getProposalFollowUpLink(proposalNumber, record?.statusId);
              const proposalNumberDisplay = (
                <span style={{ color: 'blue' }}>{proposalNumber}</span>
              );
              return targetUrl ? (
                <Link to={targetUrl} style={{ color: 'blue' }}>
                  {proposalNumber}
                </Link>
              ) : (
                proposalNumberDisplay
              );
            })()}

            {!isCustomer && <AssigneeSummary record={record} statusId={record.statusId} />}
          </div>
        ) : (
          '-'
        ),
    },
    {
      title: 'Statü',
      dataIndex: 'statusId',
      key: 'status',
      render: (statusId: number, record: any) => {
        const statusName = getStatusNameById(statusId);
        const normalizedStatusName = (statusName || '').trim();
        const followUpPath = STATUS_FOLLOWUP_ROUTES[normalizedStatusName];
        const color = tinycolor(getStatusColorById(statusId));
        const transparentColor = color.setAlpha(0.1).toRgbString();
        const transparentColorBorder = color.setAlpha(0.5).toRgbString();
        const tagEl = (
          <Tag
            style={{
              color: getStatusColorById(statusId),
              backgroundColor: transparentColor,
              borderColor: transparentColorBorder,
              borderRadius: '16px',
              padding: '5px 10px',
              fontSize: '14px',
              display: 'inline-block',
              minWidth: '100px',
              textAlign: 'center',
              fontWeight: 500,
              cursor: followUpPath ? 'pointer' : 'default',
            }}
            onClick={followUpPath ? () => navigate(followUpPath) : undefined}
          >
            {statusName}
          </Tag>
        );
        const contractRejectionNote = typeof record?.contractRejectionNote === 'string'
          ? record.contractRejectionNote.trim()
          : '';
        if (statusId === 14 && contractRejectionNote) {
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {tagEl}
              <Tooltip title={contractRejectionNote}>
                <InfoCircleOutlined style={{ color: '#1890ff' }} />
              </Tooltip>
            </span>
          );
        }
        const cancellationNote = typeof record?.cancellationNote === 'string'
          ? record.cancellationNote.trim()
          : '';
        if (statusId === 13 && cancellationNote) {
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {tagEl}
              <Tooltip title={cancellationNote}>
                <InfoCircleOutlined style={{ color: '#1890ff' }} />
              </Tooltip>
            </span>
          );
        }
        const negativeReasonMessages: string[] = [];
        if (record?.negativeReason?.text) {
          negativeReasonMessages.push(record.negativeReason.text);
        }
        if (record?.negativeReasonText) {
          negativeReasonMessages.push(record.negativeReasonText);
        }
        const negativeReasonTooltipContent =
          negativeReasonMessages.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {negativeReasonMessages.map((message, index) => (
                <span key={index}>{message}</span>
              ))}
            </div>
          ) : null;
        if (statusId === 2 && negativeReasonTooltipContent) {
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {tagEl}
              <Tooltip title={negativeReasonTooltipContent}>
                <InfoCircleOutlined style={{ color: '#1890ff' }} />
              </Tooltip>
            </span>
          );
        }
        return tagEl;
      },
    },
    // {
    //   title: 'Yenileme',
    //   dataIndex: 'renewal',
    //   key: 'renewalTracking',
    //   width: 170,
    //   render: (value: boolean | null | undefined, record: any) => {
    //     const proposalId = Number(record?.id);
    //     const isUpdating = renewalTrackingUpdateId === proposalId;
    //     const isRowDisabled =
    //       !Number.isFinite(proposalId) ||
    //       proposalId <= 0 ||
    //       (renewalTrackingUpdateId !== null && renewalTrackingUpdateId !== proposalId);

    //     return (
    //       <Switch
    //         checked={value ?? true}
    //         checkedChildren="Evet"
    //         unCheckedChildren="Hayır"
    //         loading={isUpdating}
    //         disabled={isRowDisabled}
    //         onChange={(checked) => handleRenewalTrackingToggle(record, checked)}
    //       />
    //     );
    //   },
    // },
    {
      title: '',
      key: 'action',
      render: (_text: string, record: any) => (
        <Row justify="center" align="middle">
          <Tooltip title="Yetkili Kişiler">
            <Button
              type="default"
              className="action-button icon-action-button"
              style={{ marginRight: 8 }}
              onClick={() => openAuthorizedDrawer(record)}
            >
              <UserListIcon style={{ fontSize: 20 }} />
            </Button>
          </Tooltip>
          <Dropdown
            overlay={
              record.proposalNumber !== null ? (
                <Menu>
                  {type === 1 && (record.statusId === 11 || record.statusId === 3 || record.statusId === 14 || record.statusId === 12) && (
                    <Menu.Item
                      key="sendProposal"
                      onClick={() => handleMenuClick("sendProposal", record)}
                    >
                      <span>Teklifi Gönder</span>
                    </Menu.Item>
                  )}
                  <Menu.Item
                    key="previewProposal"
                    onClick={() => window.open(`/proposals/preview/${record.id}`, '_blank', 'noopener,noreferrer')}
                  >
                    <span>Teklif Önizle</span>
                  </Menu.Item>
                  {/* <Menu.Item key="addReminder" onClick={() => handleOpenReminderDrawer(record)}>
                    Görev Ekle
                  </Menu.Item> */}
                  {canSeeProposalNotes && (
                    <Menu.Item key="notes" onClick={() => handleOpenNotesDrawer(record)}>
                      İşlem Geçmişi
                    </Menu.Item>
                  )}
                  {!isCustomer && (
                    <Menu.Item key="updateAssignees" onClick={() => openAssigneeModal(record)}>
                      Sorumlu Güncelle
                    </Menu.Item>
                  )}
                  {!isCustomer && (
                    <Menu.Item key="editDueDay" onClick={() => openDueDayModal(record)}>
                      Vade Günü Düzenle
                    </Menu.Item>
                  )}
                  {!isCustomer && (
                    <Menu.Item key="renewalSettings" onClick={() => openRenewalSettingsDrawer(record)}>
                      Yenileme Ayarları
                    </Menu.Item>
                  )}
                  {type === 1 &&
                    record.statusId !== 1 &&
                    record.statusId !== null && (
                      <Menu.Item onClick={({ key }) => handleMenuClick("download", record)} className="proposal-download" key="download">
                        <ProposalView proposalId={record.id} proposalUrl={record.proposalUrl} />
                      </Menu.Item>
                    )}
                  {record.statusId !== null &&
                    (record.statusId === 3 || record.statusId === 14 || record.statusId === 11 || (record.statusId === 10 && isAdmin) || record.statusId === 9 || record.statusId === 12) &&
                    record.statusId !== 2 && (
                      <Menu.Item onClick={({ key }) => handleMenuClick("revise", record)} key="revise">Teklifi Düzenle</Menu.Item>
                    )}

                  {type === 1 &&
                    (record.statusId === 3 || record.statusId === 14 || record.statusId === 11 || record.statusId === 12 || (record.statusId === 10 && isAdmin)) && (
                      <Menu.Item onClick={({ key }) => handleMenuClick("applyDiscount", record)} key="applyDiscount">İndirim Uygula</Menu.Item>
                    )}
                  {type === 1 && (record.statusId === 11) && (
                    <Popconfirm placement="left"
                      title="Teklifi iptal etmek istediğinize emin misiniz?"
                      onConfirm={() => handleMenuClick("cancelProposal", record)}
                      okText="Evet"
                      cancelText="Hayır"
                    >
                      <Menu.Item key="cancelProposal">Teklifi İptal Et</Menu.Item>
                    </Popconfirm>
                  )}
                  {type === 1 && (record.statusId === 3 || record.statusId === 14) && (
                    <Popconfirm placement="left"
                      title={<>Teklifi manuel olarak takip etmek<br />istediğinize emin misiniz?</>}
                      onConfirm={() => handleMenuClick("manualTracing", record)}
                      okText="Evet"
                      cancelText="Hayır"
                    >
                      <Menu.Item key="manualTracing">Manuel Takibe Al</Menu.Item>
                    </Popconfirm>
                  )}
                  {type === 1 && (record.statusId === 3 || record.statusId === 14 || record.statusId === 12 || (record.statusId === 10 && isAdmin)) && (
                    <Menu.Item onClick={({ key }) => handleMenuClick("rejectProposal", record)} key="rejectProposal">
                      Olumsuz Sonlandır
                    </Menu.Item>
                  )}
                  {type === 1 && isAdmin && record.statusId === 2 && (
                    <Menu.Item onClick={() => handleMenuClick("updateNegativeReason", record)} key="updateNegativeReason">
                      Olumsuz Nedeni Güncelle
                    </Menu.Item>
                  )}
                  {type === 1 && (record.statusId === 6 || record.statusId === 10 || record.statusId === 14) && (
                    <Menu.Item
                      key="uploadModal"
                      onClick={() => openUploadModal(record.id)}
                      disabled={!record.documentId}
                    >
                      Sözleşmeyi Görüntüle
                    </Menu.Item>
                  )}
                  {type === 1 &&
                    (record.statusId === 3 || record.statusId === 14 || record.statusId === 12 || record.statusId === 10) && (
                      <Menu.Item onClick={({ key }) => handleMenuClick("uploadDocument", record)} key="uploadDocument">{record.statusId === 10 ? "Sözleşmeyi Güncelle" : "Sözleşme Yükle"}</Menu.Item>
                    )}
                  {type === 1 && ((record.statusId === 10) && (isCentralUser || isAdmin)) && (
                    <Menu.Item
                      key="approve"
                      onClick={() => openDocumentModal(record.id)}
                      disabled={!record.documentId}
                    >
                      Sözleşmeyi Kontrol Et
                    </Menu.Item>
                  )}
                  {type === 1 && ((record.statusId === 10) && (isCentralUser || isAdmin)) && (
                    <Popconfirm
                      placement="left"
                      title="Sözleşmeyi iptal etmek istediğinize emin misiniz?"
                      onConfirm={() => handleMenuClick("cancelContract", record)}
                      okText="Evet"
                      cancelText="Hayır"
                    >
                      <Menu.Item
                        key="cancelContract"
                        disabled={!record.documentId}
                      >
                        Sözleşmeyi İptal Et
                      </Menu.Item>
                    </Popconfirm>
                  )}
                  {type === 1 && (record.statusId === 6 && isAdmin) && (
                    <Popconfirm placement="left"
                      title="Onayı iptal etmek istediğinize emin misiniz?"
                      onConfirm={() => handleMenuClick("rejectContract", record)}
                      okText="Evet"
                      cancelText="Hayır"
                    >
                      <Menu.Item key="rejectContract">
                        Sözleşme Onayını İptal Et
                      </Menu.Item>
                    </Popconfirm>
                  )}
                  {type === 2 &&
                    (record.statusId === 4 ||
                      record.statusId === 7 ||
                      record.statusId === 8) && (
                      <>
                        <Menu.Item onClick={({ key }) => handleMenuClick("download", record)} className="proposal-download" key="download">
                          <ProposalView proposalId={record.id} proposalUrl={record.proposalUrl} />
                        </Menu.Item>
                        <Menu.Item
                          key="uploadModal"
                          onClick={() => openUploadModal(record.id)}
                          disabled={!record.documentId}
                        >
                          Sözleşmeyi Görüntüle
                        </Menu.Item>
                        <Menu.Item
                          key="reportView"
                          disabled={
                            record.financialInfoUrl == null ||
                            record.invoiceAmount == null ||
                            record.reportUrl == null
                          }
                          onClick={() => handleOpenInvoiceModal(record, 'view')}
                        >
                          Faturayı Görüntüle
                        </Menu.Item>
                      </>
                    )}
                  {type === 2 &&
                    (record.statusId === 5) && (
                      <>
                        <Menu.Item onClick={({ key }) => handleMenuClick("download", record)} className="proposal-download" key="download">
                          <ProposalView proposalId={record.id} proposalUrl={record.proposalUrl} />
                        </Menu.Item>
                        <Menu.Item
                          key="uploadModal"
                          onClick={() => openUploadModal(record.id)}
                          disabled={!record.documentId}
                        >
                          Sözleşmeyi Görüntüle
                        </Menu.Item>
                      </>
                    )}
                  {type === 2 && (record.statusId === 10 || record.statusId == 6) && (
                    <Menu.Item className="proposal-download" onClick={({ key }) => handleMenuClick("download", record)} key="download">
                      <ProposalView proposalId={record.id} proposalUrl={record.proposalUrl} />
                    </Menu.Item>
                  )}
                  {type === 2 && (record.statusId === 10 || record.statusId == 6) && (
                    <Menu.Item
                      key="uploadModal"
                      onClick={() => openUploadModal(record.id)}
                      disabled={!record.documentId}
                    >
                      Sözleşmeyi Görüntüle
                    </Menu.Item>
                  )}
                  {type === 2 &&
                    (record.statusId === 10) && (
                      <Menu.Item onClick={({ key }) => handleMenuClick("uploadDocument", record)} key="uploadDocument">{record.statusId === 10 ? "Sözleşmeyi Güncelle" : "Sözleşme Yükle"}</Menu.Item>
                    )}
                  {type === 2 && record.statusId === 6 && (
                    <Menu.Item key="requestPlanningDate" onClick={({ key }) => handleMenuClick("requestPlanningDate", record)}>
                      Planlama Talebi Oluştur
                    </Menu.Item>
                  )}
                  <Menu.Item
                    key="createFromProposal"
                    onClick={() => handleOfferCreateFromProposalNavigation(record)}
                    disabled={!record.id || isCustomerCompanyProposal(record)}
                  >
                    Teklif Oluştur
                  </Menu.Item>
                </Menu>
              ) : (
                <Menu>
                  {!isFinance && (
                    <Menu.Item
                      key="create"
                      onClick={() => handleNavigateCreate(record?.company?.id)}
                      disabled={isPartner?.toString() === 'false' || isCustomerCompanyProposal(record)}
                    >
                      Teklif Oluştur
                    </Menu.Item>
                  )}
                </Menu>
              )
            }
          >
            <Button
              type="default"
              className="d-button action-button"
            >
              İşlemler
            </Button>
          </Dropdown>
        </Row>
      ),
    }
  ];

  const getDataIndexValue = (record: any, dataIndex?: string | string[]) => {
    if (!dataIndex) return undefined;
    if (Array.isArray(dataIndex)) {
      return dataIndex.reduce((acc, key) => acc?.[key], record);
    }
    return record?.[dataIndex];
  };

  const renderColumnValue = (key: string, record: any) => {
    const column = columns.find((col: any) => col.key === key);
    if (!column) return null;
    const value = getDataIndexValue(record, column.dataIndex as any);
    if (column.render) {
      return column.render(value, record);
    }
    return value ?? '-';
  };

  const handleSearch = useCallback(
    debounce((value: string) => {
      setSearchText(getEffectiveProposalSearchText(value));
      setCurrentPage(1);
    }, 500),
    []
  );

  const handleStatusChange = (value: number[]) => {
    setSelectedStatusIds(value);
    setCurrentPage(1);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleSearch(e.target?.value);
  };

  useEffect(() => {
    return () => {
      handleSearch.cancel();
      proposalListAbortControllerRef.current?.abort();
      proposalListAbortControllerRef.current = null;
    };
  }, [handleSearch]);

  const formatSummaryDays = (minutes: any) => {
    const parsedMinutes = Number(minutes);
    if (!parsedMinutes || Number.isNaN(parsedMinutes) || parsedMinutes <= 0) {
      return '0 (0.00)';
    }

    return `${Math.ceil(parsedMinutes / 480)} (${(parsedMinutes / 480).toFixed(2)})`;
  };

  const followUpSummaryContent = canViewFollowUpSummary ? (
    <AntSpace
      size={[10, 4]}
      wrap
      style={{
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 12,
        maxWidth: 760,
        opacity: summaryLoading ? 0.65 : 1,
      }}
    >
      {followUpSummary.map((summary) => (
        <AntSpace key={summary.statusId} size={[6, 2]} wrap style={{ alignItems: 'center', justifyContent: 'center'  }}>
          {canViewFollowUpTotalPrice && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Toplam Teklif Tutarı: <strong>{formatToTL(Number(summary.totalPrice) || 0)}</strong>
            </Text>
          )}
          {canViewFollowUpEffort && (
            <>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Makine: <strong>{formatSummaryDays(summary.machineMinutes)}</strong>
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Elektrik: <strong>{formatSummaryDays(summary.electricMinutes)}</strong>
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                İş Hijyeni: <strong>{formatSummaryDays(summary.hygieneMinutes)}</strong>
              </Text>
            </>
          )}
        </AntSpace>
      ))}
      {summaryLoading && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          Güncelleniyor...
        </Text>
      )}
    </AntSpace>
  ) : null;

  useEffect(() => {
    const searchFromUrl = searchParams.get('search');
    if (!searchFromUrl) {
      return;
    }
    const normalizedSearch = getEffectiveProposalSearchText(searchFromUrl);
    if (normalizedSearch !== searchText) {
      handleSearch(searchFromUrl);
    }
  }, [searchParams, searchText, handleSearch]);

  const handleOfferEditNavigation = (id: number) => {
    navigate(`/proposals/create?proposalId=${id}`);
  };

  const handleOfferCreateFromProposalNavigation = (record: any) => {
    const id = record?.id;
    if (!id) return;
    const source = getProposalStatusId(record) === 4 ? 'upcoming-projects' : 'renewable-proposals';
    navigate(buildOpportunityProposalCreateLink(id, source));
  };

  const filterClear = () => {
    setSelectedStatusIds([]);
    setSelectedNegativeReasonIds([]);
    setSelectedRenewal(null);
    setDateRange([null, null]);
    setOnlyMyCompanies(false);
    setSelectedCompanyId(null);
    setSelectedCustomerId(null);
    setSelectedCityId(null);
    setSelectedDistrictId(null);
    setCurrentPage(1);
    setFilter(filter + 1);
  };

  const getFilterProposal = () => {
    setCurrentPage(1);
    setFilter(filter + 1);
    setOpen(false);
  };

  const handleNavigateCreate = (companyId: any = null) => {
    if (companyId == null) {
      return navigate('/proposals/create');
    }
    navigate(`/proposals/create?companyId=${companyId}`);
  };

  // Date Range seçimi
  const onDateRangeChange = (dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null) => {
    if (dates) {
      setDateRange([
        dates[0]?.format('YYYY-MM-DD') ?? null,
        dates[1]?.format('YYYY-MM-DD') ?? null
      ]);
    } else {
      setDateRange([null, null]);
    }
  };

  const handleCloseInvoiceModal = () => {
    setIsInvoiceModalVisible(false);
  };

  // 1) permissions’ı localStorage'dan çek
  useEffect(() => {
    const storedPermissions = localStorage.getItem('permissions');
    if (storedPermissions) {
      try {
        const parsedPermissions = JSON.parse(storedPermissions);

        // Eğer JSON içeriği gerçekten bir array ise set et
        if (Array.isArray(parsedPermissions)) {
          setPermissions(parsedPermissions);
        } else {
          console.error('Permissions hatalı formatta:', parsedPermissions);
        }
      } catch (err) {
        console.error('Permissions parse hatası:', err);
      }
    }
  }, [statuses, defaultStatusIds]);

  const rangePickerValue: [dayjs.Dayjs | null, dayjs.Dayjs | null] = [
    dateRange[0] ? dayjs(dateRange[0]) : null,
    dateRange[1] ? dayjs(dateRange[1]) : null,
  ];

  return (
    <>
      <Card className="br-30px mb-120px proposal-card">
        <Row
          gutter={[16, 16]}
          align="middle"
          justify="space-between"
          className="mb-20px"
        >
          <Col>
            <Row align="bottom">
              {type === 1 ? (
                <>
                  <Title level={4} style={{ margin: 0 }}>
                    Teklif Takip Listesi
                  </Title>
                  <Text type="secondary" className="ms-5" ><strong>({totalItems})</strong> kayıt listelendi</Text>
                </>
              ) : (
                <>
                  <Title level={4} style={{ margin: 0 }}>
                    İş Takip Listesi
                  </Title>
                  <Text type="secondary" className="ms-5" ><strong>({totalItems})</strong> kayıt listelendi</Text>
                </>
              )}
            </Row>
          </Col>
          <Col>
            <Row gutter={[16, 16]} justify={"center"} align={"middle"}>
              {followUpSummaryContent && (
                <Col>
                  {followUpSummaryContent}
                </Col>
              )}
              <Col>
                <Input
                  allowClear
                  placeholder="Ara"
                  prefix={<SearchOutlined style={{ color: '#b8b9bc' }} />}
                  className="d-input d-input-filter mr-8px d-input-filter"
                  style={{ width: 200 }}
                  onChange={handleInputChange}
                />
              </Col>
              <Col>
                <Button type="primary" onClick={showDrawer} disabled={loading}>
                  Filtrele
                </Button>
              </Col>
              <Col>
                {type === 1 && (
                  <Tooltip title="Dışa Aktar">
                    <Button
                      shape='circle'
                      className='excel-icon-button'
                      onClick={handleExportExcel}
                      aria-label='Excel indir'
                    >
                      <FileExcelOutlined />
                    </Button>
                  </Tooltip>
                )}
              </Col>
              <Col>
                <Row justify={'end'} align="middle">
                  {type === 1 && !isFinance && (
                    <Tooltip
                      title={
                        isPartner?.toString() === 'false'
                          ? 'Teklif oluşturabilmek için profil sayfanızda yer alan Sözleşme bölümündeki İş Ortaklığı Sözleşmesini onaylamanız gerekmektedir.'
                          : ''
                      }
                    >
                      <Button
                        type="primary"
                        className="d-button dark-button width-auto"
                        onClick={() => handleNavigateCreate(null)}
                        disabled={isPartner?.toString() === 'false'}
                      >
                        Teklif Oluştur
                      </Button>
                    </Tooltip>
                  )}
                </Row>
              </Col>
            </Row>
          </Col>
        </Row>
        <Drawer
          title="Filtre"
          closable={{ 'aria-label': 'Close Button' }}
          onClose={onClose}
          open={open}
        >
          <Row gutter={[16, 16]}>
            <Col span={24}>
              {(isAdmin || isCentralUser) && (
                <Checkbox
                  checked={onlyMyCompanies}
                  onChange={(e) => setOnlyMyCompanies(e.target.checked)}
                  style={{ marginLeft: 16 }}
                >
                  Sadece kendi tekliflerimi listele
                </Checkbox>
              )}
            </Col>
            <Col span={24}>
              <Select
                mode="multiple"
                showSearch
                placeholder="Statü"
                style={{ width: '100%' }}
                value={selectedStatusIds}
                onChange={handleStatusChange}
                optionFilterProp="children"
                filterOption={(input, option: any) =>
                  normalizeTurkish(option?.children as string).includes(normalizeTurkish(input))
                }
              >
                {statuses
                  .slice()
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((status) => (
                    <Select.Option key={status.id} value={status.id}>
                      {status.name}
                    </Select.Option>
                  ))}
              </Select>
            </Col>

          

            {(isAdmin || isCentralUser) && (
              <>
                <Col span={24}>
                  <Select
                    showSearch
                    allowClear
                    placeholder="Kullanıcı"
                    style={{ width: '100%' }}
                    value={selectedCustomerId}
                    onChange={handleCustomerChange}
                    optionFilterProp="label"
                    filterOption={(input, option: any) =>
                      normalizeTurkish(option?.label).includes(normalizeTurkish(input))
                    }
                  >
                    {customerList.map((customer: any) => {
                      const label = `${customer?.firstName} ${customer?.lastName} - ${customer?.email}`;
                      return (
                        <Select.Option key={customer.id} value={customer.id} label={label}>
                          {label}
                        </Select.Option>
                      );
                    })}
                  </Select>
                </Col>

                <Col span={24}>
                  <Select
                    showSearch
                    allowClear
                    placeholder="Firma"
                    style={{ width: '100%' }}
                    value={selectedCompanyId}
                    onChange={handleCompanyChange}
                    optionFilterProp="label"
                    filterOption={(input, option: any) =>
                      normalizeTurkish(option?.label).includes(normalizeTurkish(input))
                    }
                  >
                    {companyList.map((company: any) => (
                      <Select.Option
                        key={company.id}
                        value={company.id}
                        label={company.name}
                      >
                        {company.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Col>
              </>
            )}

            <Col span={24}>
              <Select
                showSearch
                allowClear
                placeholder="İl"
                style={{ width: '100%' }}
                value={selectedCityId}
                onChange={handleCityChange}
                optionFilterProp="label"
                filterOption={(input, option: any) =>
                  normalizeTurkish(option?.label).includes(normalizeTurkish(input))
                }
              >
                {cities
                  ?.filter((city) => cityIdsToShow.includes(city.id))
                  .map((city: any) => (
                    <Select.Option key={city.id} value={city.id} label={city.name}>
                      {city.name}
                    </Select.Option>
                  ))}
              </Select>
            </Col>

            <Col span={24}>
              <Select
                showSearch
                allowClear
                placeholder="İlçe"
                style={{ width: '100%' }}
                value={selectedDistrictId}
                onChange={handleDistrictChange}
                optionFilterProp="label"
                filterOption={(input, option: any) =>
                  normalizeTurkish(option?.label).includes(normalizeTurkish(input))
                }
              >
                {districts.map((district: any) => (
                  <Select.Option key={district.id} value={district.id} label={district.name}>
                    {district.name}
                  </Select.Option>
                ))}
              </Select>
            </Col>
            <Col span={24}>
              {/* Tarih Aralığı */}
              <RangePicker
                format="DD/MM/YYYY"
                locale={locale}
                className="d-input-filter mr-8px d-input-filter"
                style={{ marginRight: 10 }}
                value={rangePickerValue}
                onChange={onDateRangeChange}
              />
            </Col>
              <Col span={24}>
              <Select
                mode="multiple"
                showSearch
                allowClear
                placeholder="Olumsuzluk Nedeni"
                style={{ width: '100%' }}
                value={selectedNegativeReasonIds}
                onChange={setSelectedNegativeReasonIds}
                optionFilterProp="label"
                filterOption={(input, option: any) =>
                  normalizeTurkish(option?.label).includes(normalizeTurkish(input))
                }
              >
                {negativeReasons.map((reason) => (
                  <Select.Option key={reason.id} value={reason.id} label={reason.text}>
                    {reason.text}
                  </Select.Option>
                ))}
              </Select>
            </Col>

            <Col span={24}>
              <Select
                allowClear
                placeholder="Yenileme Takibi"
                style={{ width: '100%' }}
                value={selectedRenewal}
                onChange={(value) => setSelectedRenewal(value ?? null)}
              >
                <Select.Option value={true}>Evet</Select.Option>
                <Select.Option value={false}>Hayır</Select.Option>
              </Select>
            </Col>
          </Row>
          <Row gutter={[16, 16]} className="justify-content-end mt-16px">
            <Col>
              <Button
                type="default"
                onClick={() => filterClear()}
              >
                Temizle
              </Button>
            </Col>
            <Col>
              <Button type="primary" onClick={() => getFilterProposal()}>
                Filtrele
              </Button>
            </Col>
          </Row>
        </Drawer>
        {isMobile ? (
          <List
            loading={loading}
            dataSource={filteredProposals}
            rowKey={(record: any) => record.id || record.proposalNumber || record.key}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: totalItems,
              onChange: (page, pgSize) => {
                setCurrentPage(page);
                setPageSize(pgSize || 50);
              },
              showSizeChanger: true,
              pageSizeOptions: ['50', '100', '200', '500'],
              defaultPageSize: 50,
            }}
            renderItem={(record: any) => (
              <List.Item key={record.id}>
                <Card size="small" style={{ width: '100%' }}>
                  <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    {renderColumnValue('companyName', record)}
                    <Space direction="vertical" size={2} style={{ width: '100%' }}>
                      <Text type="secondary">Teklif No</Text>
                      {renderColumnValue('proposalNumber', record) ?? '-'}
                    </Space>
                    <Space direction="vertical" size={2} style={{ width: '100%' }}>
                      <Text type="secondary">Teklif Tarihi</Text>
                      {renderColumnValue('createdAt', record) ?? '-'}
                    </Space>
                    <Space direction="vertical" size={2} style={{ width: '100%' }}>
                      <Text type="secondary">Teklif Tutarı</Text>
                      {renderColumnValue('totalPrice', record) ?? '-'}
                    </Space>
                    <Space direction="vertical" size={2} style={{ width: '100%' }}>
                      <Text type="secondary">Statü</Text>
                      {renderColumnValue('status', record) ?? '-'}
                    </Space>
                    <div>{renderColumnValue('action', record)}</div>
                  </Space>
                </Card>
              </List.Item>
            )}
          />
        ) : (
          <Table
            loading={loading}
            scroll={{ x: 768 }}
            columns={columns}
            dataSource={filteredProposals}
            rowKey={(record, key) => record.id || key}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: totalItems,
              onChange: (page, pgSize) => {
                setCurrentPage(page);
                setPageSize(pgSize || 50);
              },
              showSizeChanger: true,
              pageSizeOptions: ['50', '100', '200', '500'],
              defaultPageSize: 50,
            }}
            onChange={handleTableChange}
          />
        )}

        <NegativeProposalReasonModal
          open={rejectModalVisible}
          onCancel={handleRejectModalCancel}
          onSubmit={handleRejectSubmit}
          confirmLoading={rejectLoading}
          title={rejectActionType === 'updateReason' ? 'Teklifin Olumsuz Sonuçlanma Gerekçesini Güncelle' : 'Teklifin Olumsuz Sonuçlanma Nedeni'}
          submitText={rejectActionType === 'updateReason' ? 'Nedeni Güncelle' : 'Olumsuz Olarak Sonlandır'}
          initialValues={rejectInitialValues}
          showSurveySwitch={rejectActionType !== 'updateReason'}
        />

        <Modal
          title="Sorumlu Güncelle"
          open={assigneeModalVisible}
          onCancel={handleCloseAssigneeModal}
          onOk={handleAssigneeUpdate}
          confirmLoading={assigneeUpdateLoading}
          okText="Kaydet"
          cancelText="İptal"
          destroyOnClose
        >
          <Form layout="vertical" form={assigneeForm}>
            {assigneeSettings.map((setting) => {
              const fieldName = ASSIGNEE_SETTING_FIELD_MAP[setting.key as typeof ASSIGNEE_SETTING_KEYS[number]];
              if (!fieldName) return null;
              const label = setting.title || ASSIGNEE_SETTING_TITLE_MAP[setting.key] || setting.key;
              const isOfferAssignee = fieldName === 'offerAssigneeUserId';
              const isSelectDisabled = !isOfferAssignee && !isAdmin;
              return (
                <Form.Item key={setting.key} name={fieldName} label={label}>
                  <Select
                    allowClear
                    showSearch
                    placeholder={`${label} seçiniz`}
                    optionFilterProp="children"
                    loading={assigneeModalLoading}
                    disabled={isSelectDisabled}
                    filterOption={(input, option) => {
                      const child = option?.children as any;
                      const labelText =
                        typeof child === 'string'
                          ? child
                          : child && typeof child.toString === 'function'
                            ? child.toString()
                            : '';
                      return normalizeTurkish(labelText).includes(normalizeTurkish(input));
                    }}
                  >
                    {(() => {
                      const currentValue = assigneeForm.getFieldValue(fieldName);
                      const currentId = Number(currentValue);
                      const options = [...assignableUsers];
                      if (!Number.isNaN(currentId) && currentId > 0) {
                        const exists = options.some((user) => Number(user?.id) === currentId);
                        if (!exists) {
                          const fallbackUser =
                            assignableUsersAll.find((user) => Number(user?.id) === currentId) ||
                            {
                              id: currentId,
                              firstName: 'Kullanıcı',
                              lastName: `#${currentId}`,
                            };
                          options.push(fallbackUser);
                        }
                      }
                      return options.map((user) => {
                        const optionId = Number(user?.id);
                        if (Number.isNaN(optionId) || optionId <= 0) {
                          return null;
                        }
                        const fullName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
                        const labelText = fullName || user.email || user.id;
                        return (
                          <Select.Option key={optionId} value={String(optionId)}>
                            {labelText}
                          </Select.Option>
                        );
                      });
                    })()}
                  </Select>
                </Form.Item>
              );
            })}
            {assigneeSettings.length === 0 && (
              <Text type="secondary">Sorumlu ayarları yüklenemedi.</Text>
            )}
          </Form>
        </Modal>

        <Modal
          title="Vade Günü Düzenle"
          open={isDueDayModalVisible}
          onCancel={handleCloseDueDayModal}
          onOk={handleDueDayUpdate}
          confirmLoading={dueDayUpdateLoading}
          okText="Kaydet"
          cancelText="İptal"
          destroyOnClose
        >
          <Form layout="vertical" form={dueDayForm}>
            <Form.Item
              label="Vade Günü"
              name="dueDay"
              rules={[
                { required: true, message: 'Vade günü giriniz' },
                { type: 'number', min: 1, message: 'Vade günü 1 veya daha büyük olmalıdır' },
              ]}
            >
              <InputNumber style={{ width: '100%' }} min={1} />
            </Form.Item>
          </Form>
        </Modal>

        <RenewalSettingsDrawer
          open={renewalSettingsOpen}
          proposal={renewalSettingsTargetProposal}
          loading={renewalSettingsLoading}
          onClose={closeRenewalSettingsDrawer}
          onSubmit={handleRenewalSettingsSubmit}
        />

        <DiscountModal
          visible={isDiscountModalVisible}
          totalPrice={
            selectedProposal
              ? selectedProposal.originalPrice ?? selectedProposal.totalPrice ?? null
              : null
          }
          existingDiscountAmount={selectedProposal?.discountedPrice ?? null}
          onCancel={handleModalCancel}
          onSubmit={handleDiscountSubmit}
          disableSubmit={Boolean(selectedProposal?.isTotalPriceSet)}
          submitLoading={isDiscountSubmitLoading}
          maxDiscountRate={currentUserMaxDiscountRate}
        />

        {discountPdfArchiveProposalId && (
          <PdfUpload
            key={`discount-pdf-archive-${discountPdfArchiveProposalId}`}
            proposalId={discountPdfArchiveProposalId}
            isCreate={false}
            silent
            shouldNavigate={false}
            onSuccess={handleDiscountPdfArchiveSuccess}
            onError={handleDiscountPdfArchiveError}
          />
        )}

        {/* Planlama Modal */}
        {isPlanningModalVisible && (
          <PlanningModal
            setIsModalVisible={setIsPlanningModalVisible}
            isModalVisible={isPlanningModalVisible}
            proposalId={selectedProposalId!}
            isCustomer={true}
          />
        )}

        {/* Sözleşme Yükleme Modal */}
        {isUploadModalVisible && (
          <DocumentUploadModal
            setIsModalVisible={setIsUploadModalVisible}
            isModalVisible={isUploadModalVisible}
            proposalId={selectedProposalId!}
            isCustomer={true}
            type={documentModalType}
          />
        )}

        {isDocumentModalVisible && selectedProposalId !== null && (
          <ApproveDocument
            setIsModalVisible={setIsDocumentModalVisible}
            isModalVisible={isDocumentModalVisible}
            proposalId={selectedProposalId}
            isCustomer={true}
            type={2}
          />
        )}
      </Card>

      <Modal
        title="Teklif İptal Nedeni"
        open={cancelProposalModalVisible}
        onCancel={handleCancelProposalModalClose}
        onOk={handleCancelProposalSubmit}
        confirmLoading={cancelProposalLoading}
        okText="Kaydet"
        cancelText="İptal"
        destroyOnClose
      >
        <Form
          form={cancelProposalForm}
          layout="vertical"
          preserve={false}
        >
          <Form.Item
            label="İptal Nedeni"
            name="cancellationNote"
            rules={[
              { required: true, whitespace: true, message: 'İptal nedeni zorunludur.' },
            ]}
          >
            <TextArea
              rows={4}
              placeholder="Teklif iptal nedeni giriniz"
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Teklifi göndermek istediğinize emin misiniz?"
        open={isSendProposalModalVisible}
        onCancel={() => {
          setSendProposalModalVisible(false);
          setSendProposalScheduleAt(null);
        }}
        onOk={handleSendProposalConfirm}
        confirmLoading={sendProposalLoading}
        okText="Gönder"
        cancelText="İptal"
      >
        <p>
          Dilerseniz teklif gönderimini ileri bir tarih/saat için planlayabilirsiniz.
          Tarih seçilmezse gönderim hemen gerçekleşir.
        </p>
        <Form layout="vertical">
          <Form.Item
            label="E-posta Gönderim Tarihi (isteğe bağlı)"
          >
            <DatePicker
              locale={locale}
              showTime={{ minuteStep: 5 }}
              format="DD/MM/YYYY HH:mm"
              style={{ width: '100%' }}
              value={sendProposalScheduleAt}
              onChange={(value) => setSendProposalScheduleAt(value)}
              allowClear
              placeholder="GG/AA/YYYY SS:DD"
              disabledDate={isEmailSendDateDisabled}
              disabledTime={getEmailSendDisabledTime}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Rapor & Fatura Modal */}
      {isInvoiceModalVisible && selectedProposalId && (
        <ReportInvoiceModal
          visible={isInvoiceModalVisible}
          onClose={handleCloseInvoiceModal}
          onSubmit={() => null}
          initialValues={invoiceModalData}
          type={invoiceModalType}
        />
      )}

      <ProposalProcessHistoryDrawer
        open={isNotesDrawerOpen}
        proposal={noteTargetProposal}
        isAdmin={isAdmin}
        statuses={statuses}
        titleMode="proposal-list"
        onClose={handleCloseNotesDrawer}
      />

      {/* Yetkili Kişiler Drawer */}
      <Drawer
        title="Yetkili Kişiler"
        open={isAuthorizedDrawerVisible}
        onClose={() => setIsAuthorizedDrawerVisible(false)}
        width={1000}
        destroyOnClose
        footer={
          <div style={{ textAlign: 'right' }}>
            <Button style={{ marginRight: 8 }} onClick={() => setIsAuthorizedDrawerVisible(false)}>
              İptal
            </Button>
            <Button
              type="primary"
              className="d-button dark-button width-auto"
              onClick={handleSaveAuthorizedPersons}
              disabled={!selectedAuthorizedIds || selectedAuthorizedIds.length === 0}
            >
              Kaydet
            </Button>
          </div>
        }
      >
        {authorizedCompanyId && (
          <AuthorizedPersonTable
            companyId={authorizedCompanyId}
            // Enable selection for linking to proposal
            showSelection={true}
            // Drawer kuralı: iletişim konuları olmayanlar seçilemesin; diğerleri seçilebilsin
            selectionFilter={'hasAny'}
            showSelectionMessage={false}
            // type can remain false here; behavior is controlled by selectionFilter
            selectedAuthorizedPersonIds={selectedAuthorizedIds}
            setSelectedAuthorizedPersonIds={setSelectedAuthorizedIds}
          />
        )}
      </Drawer>

    </>
  );
};

export default ProposalList;
